// @owners { team: patients-team }
import { SPACING } from '@alto/design-library-tokens';
import { MdPadding, SmSpacing } from '@alto/design-system';
import {
  InsuranceBinInput,
  InsuranceGroupInput,
  InsuranceMemberIdInput,
  InsuranceNicknameInput,
  InsurancePcnInput,
  InsuranceRelationshipInputSelect,
} from '@alto/features';
import React from 'react';
import type { Control, FieldErrors } from 'react-hook-form';
import { useController } from 'react-hook-form';
import { RELATIONSHIP_CODES } from '~shared/constants';
import { type Values } from '~shared/features/insurances/helpers/getInitialInsuranceFormValues';
import { createValidator, validatePresentIf, validatePresentWithMessage } from '~shared/validations';

type Props = {
  readonly control: Control<Values>;
  readonly isSubmitted: boolean;
};

export const validate = createValidator<Values>({
  relationship_code: validatePresentWithMessage('Relationship'),
  first_name: validatePresentIf((_, values) => {
    return values.relationship_code !== RELATIONSHIP_CODES.SELF;
  }, 'First Name'),
  last_name: validatePresentIf((_, values) => {
    return values.relationship_code !== RELATIONSHIP_CODES.SELF;
  }, 'Last Name'),
  member_id: validatePresentWithMessage('Member ID'),
  bin: validatePresentWithMessage('BIN'),
});

export const InsuranceFormFields = ({ control, isSubmitted }: Props) => {
  const memberId = useController({ control, name: 'member_id' });
  const bin = useController({ control, name: 'bin' });
  const group = useController({ control, name: 'group' });
  const pcn = useController({ control, name: 'pcn' });
  const relationshipCode = useController({ control, name: 'relationship_code' });
  const firstName = useController({ control, name: 'first_name' });
  const lastName = useController({ control, name: 'last_name' });
  const nickname = useController({ control, name: 'nickname' });

  const getError = (err: FieldErrors<Values>[keyof Values]) => (isSubmitted ? err?.message : undefined);

  return (
    <>
      <SmSpacing />
      <MdPadding
        leftPadding={SPACING.STATIC.LG}
        rightPadding={SPACING.STATIC.LG}
      >
        <InsuranceMemberIdInput
          error={getError(memberId.fieldState.error)}
          onChange={memberId.field.onChange}
          value={memberId.field.value ?? ''}
        />
      </MdPadding>
      <MdPadding
        leftPadding={SPACING.STATIC.LG}
        rightPadding={SPACING.STATIC.LG}
      >
        <InsuranceBinInput
          error={getError(bin.fieldState.error)}
          onChange={bin.field.onChange}
          value={bin.field.value ?? ''}
        />
      </MdPadding>
      <MdPadding
        leftPadding={SPACING.STATIC.LG}
        rightPadding={SPACING.STATIC.LG}
      >
        <InsuranceGroupInput
          error={getError(group.fieldState.error)}
          value={group.field.value ?? ''}
          onChange={group.field.onChange}
        />
      </MdPadding>
      <MdPadding
        leftPadding={SPACING.STATIC.LG}
        rightPadding={SPACING.STATIC.LG}
      >
        <InsurancePcnInput
          error={getError(pcn.fieldState.error)}
          onChange={pcn.field.onChange}
          value={pcn.field.value ?? ''}
        />
      </MdPadding>
      <MdPadding
        leftPadding={SPACING.STATIC.LG}
        rightPadding={SPACING.STATIC.LG}
      >
        <InsuranceRelationshipInputSelect
          firstName={firstName.field.value}
          firstNameError={getError(firstName.fieldState.error)}
          lastName={lastName.field.value}
          lastNameError={getError(lastName.fieldState.error)}
          onChangeFirstName={firstName.field.onChange}
          onChangeLastName={lastName.field.onChange}
          onChangeRelationship={relationshipCode.field.onChange}
          relationshipCode={relationshipCode.field.value ?? ''}
          relationshipCodeError={getError(relationshipCode.fieldState.error)}
        />
      </MdPadding>
      <MdPadding
        leftPadding={SPACING.STATIC.LG}
        rightPadding={SPACING.STATIC.LG}
      >
        <InsuranceNicknameInput
          error={getError(nickname.fieldState.error)}
          value={nickname.field.value ?? ''}
          onChange={nickname.field.onChange}
        />
      </MdPadding>
      <SmSpacing />
    </>
  );
};
